import React, { useState, useEffect } from 'react';
import { Header } from '../layout/Header';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { ScrollArea } from '../components/ui/scroll-area';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from '../components/ui/use-toast';
import { useAuth } from '../contexts/AuthContext';
import contactService from '../services/contactService';
import {
  Search,
  Plus,
  RefreshCw,
  Mail,
  Phone,
  Building,
  Briefcase,
  Calendar,
  Edit,
  Trash2,
  Download,
  Upload,
  Loader2
} from 'lucide-react';

export default function ContactManagerPage() {
  const { user } = useAuth();
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState('all');
  const [customerList, setCustomerList] = useState([]);
  const [selectedContact, setSelectedContact] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const [activeTab, setActiveTab] = useState('all');
  
  const isBM = user?.role === 'BM';

  useEffect(() => {
    fetchAllContacts();
    fetchCustomerList();
  }, []);

  const fetchAllContacts = async () => {
    setLoading(true);
    try {
      // For now, we'll fetch contacts by making multiple requests to customer endpoints
      // In a real implementation, you'd have a dedicated endpoint for all contacts
      const response = await fetch('/customers/all');
      const customers = await response.json();
      
      const allContacts = [];
      for (const customer of customers.slice(0, 10)) { // Limit to first 10 customers for demo
        try {
          const contacts = await contactService.getCustomerContacts(customer.id || customer.customer_id);
          allContacts.push(...contacts);
        } catch (err) {
          console.error(`Failed to fetch contacts for customer ${customer.id}:`, err);
        }
      }
      
      setContacts(allContacts);
    } catch (error) {
      console.error('Error fetching contacts:', error);
      toast({
        title: 'Error',
        description: 'Failed to load contacts',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchCustomerList = async () => {
    try {
      const response = await fetch('/customers/all');
      const customers = await response.json();
      setCustomerList(customers);
    } catch (error) {
      console.error('Error fetching customers:', error);
    }
  };

  const handleCreateContact = () => {
    // This would open a create contact dialog
    toast({
      title: 'Create Contact',
      description: 'Contact creation dialog would open here'
    });
  };

  const handleEditContact = (contact) => {
    setSelectedContact(contact);
    setEditDialogOpen(true);
  };

  const handleDeleteContact = async (contactId) => {
    try {
      // In a real implementation, you'd have a delete endpoint
      toast({
        title: 'Success',
        description: 'Contact deleted successfully'
      });
      fetchAllContacts();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to delete contact',
        variant: 'destructive'
      });
    }
  };

  const handleSyncFromHubSpot = async () => {
    if (!isBM) {
      toast({
        title: 'Permission Denied',
        description: 'Only Business Managers can sync contacts',
        variant: 'destructive'
      });
      return;
    }

    setSyncing(true);
    try {
      const result = await contactService.syncContactsFromHubSpot(100);
      setSyncResult(result);
      toast({
        title: 'Sync Complete',
        description: `Synced ${result.synced_count} contacts`
      });
      fetchAllContacts();
    } catch (error) {
      console.error('Error syncing from HubSpot:', error);
      toast({
        title: 'Sync Failed',
        description: 'Failed to sync contacts from HubSpot',
        variant: 'destructive'
      });
    } finally {
      setSyncing(false);
    }
  };

  const filteredContacts = contacts.filter(contact => {
    const matchesSearch = 
      contact.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      contact.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      contact.phone?.includes(searchTerm) ||
      contact.position?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesCustomer = selectedCustomer === 'all' || contact.customer_id === selectedCustomer;
    
    return matchesSearch && matchesCustomer;
  });

  const getInitials = (name) => {
    return name
      ?.split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2) || '??';
  };

  return (
    <div>
      <Header />
      <div className="container mx-auto px-4 py-6">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-2xl font-bold">Contact Manager</h1>
            <p className="text-muted-foreground mt-1">
              Manage all customer contacts and sync with HubSpot
            </p>
          </div>
          <div className="flex gap-2">
            {isBM && (
              <Button
                variant="outline"
                onClick={handleSyncFromHubSpot}
                disabled={syncing}
              >
                {syncing ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Syncing...
                  </>
                ) : (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Sync from HubSpot
                  </>
                )}
              </Button>
            )}
            <Button onClick={handleCreateContact}>
              <Plus className="h-4 w-4 mr-2" />
              Add Contact
            </Button>
          </div>
        </div>

        {syncResult && syncResult.errors?.length > 0 && (
          <Card className="mb-4 border-amber-200 bg-amber-50">
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Sync Warnings</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-amber-800">
                {syncResult.errors.length} contacts couldn't be synced. Check the logs for details.
              </p>
            </CardContent>
          </Card>
        )}

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="all">All Contacts</TabsTrigger>
            <TabsTrigger value="recent">Recently Updated</TabsTrigger>
            <TabsTrigger value="synced">HubSpot Synced</TabsTrigger>
          </TabsList>

          <TabsContent value={activeTab} className="mt-4">
            <Card>
              <CardHeader>
                <div className="flex flex-col sm:flex-row gap-4">
                  <div className="flex-1 relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Search contacts..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                  <Select value={selectedCustomer} onValueChange={setSelectedCustomer}>
                    <SelectTrigger className="w-full sm:w-64">
                      <SelectValue placeholder="Filter by customer" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Customers</SelectItem>
                      {customerList.map(customer => (
                        <SelectItem 
                          key={customer.id || customer.customer_id} 
                          value={customer.id || customer.customer_id}
                        >
                          {customer.customername || customer.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                {loading ? (
                  <div className="flex items-center justify-center p-8">
                    <Loader2 className="h-8 w-8 animate-spin" />
                  </div>
                ) : filteredContacts.length === 0 ? (
                  <div className="text-center text-muted-foreground p-8">
                    {searchTerm || selectedCustomer !== 'all' 
                      ? 'No contacts found matching your filters'
                      : 'No contacts available'}
                  </div>
                ) : (
                  <ScrollArea className="h-[600px]">
                    <div className="divide-y">
                      {filteredContacts.map(contact => (
                        <div
                          key={contact.id}
                          className="p-4 hover:bg-muted/50 transition-colors"
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex items-start space-x-3">
                              <Avatar>
                                <AvatarFallback>{getInitials(contact.name)}</AvatarFallback>
                              </Avatar>
                              <div>
                                <h4 className="font-semibold">{contact.name}</h4>
                                {contact.position && (
                                  <Badge variant="secondary" className="mt-1">
                                    {contact.position}
                                  </Badge>
                                )}
                                <div className="mt-2 space-y-1 text-sm text-muted-foreground">
                                  <div className="flex items-center gap-2">
                                    <Mail className="h-3 w-3" />
                                    <a href={`mailto:${contact.email}`} className="hover:underline">
                                      {contact.email}
                                    </a>
                                  </div>
                                  {contact.phone && (
                                    <div className="flex items-center gap-2">
                                      <Phone className="h-3 w-3" />
                                      <a href={`tel:${contact.phone}`} className="hover:underline">
                                        {contact.phone}
                                      </a>
                                    </div>
                                  )}
                                  {contact.metadata?.company && (
                                    <div className="flex items-center gap-2">
                                      <Building className="h-3 w-3" />
                                      <span>{contact.metadata.company}</span>
                                    </div>
                                  )}
                                </div>
                                {contact.hubspot_contact_id && (
                                  <Badge variant="outline" className="mt-2">
                                    HubSpot ID: {contact.hubspot_contact_id}
                                  </Badge>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleEditContact(contact)}
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                              {isBM && (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => handleDeleteContact(contact.id)}
                                >
                                  <Trash2 className="h-4 w-4 text-destructive" />
                                </Button>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Edit Contact Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Contact</DialogTitle>
            <DialogDescription>
              Update contact information
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {/* Add edit form fields here */}
            <p className="text-muted-foreground">Edit form would go here</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
              Cancel
            </Button>
            <Button>Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}